import CustomField from 'elements/field/CustomField'
import DependentInput from 'elements/input/DependentInput'
import InfoTooltip from 'elements/tooltip/InfoTooltip'
import { Grid } from 'opensolar-ui'
import { FormDataConsumer, NumberInput, SelectInput, useTranslate } from 'react-admin'
import { requiredPositiveOrZero, validRange } from 'validations'
import {
  DependentFieldContainer,
  FieldWrapper,
  TooltipContainer,
  TooltipDescriptionCell,
  TooltipFooter,
  TooltipHeader,
  TooltipIconWrapper,
  TooltipLabel,
  TooltipRow,
  TooltipTable,
} from '../ProjectConfigurations.styles'

export const ReplacementCostMethodFields = ({
  componentType,
  costMethodField,
  inflationField,
  fixedValueField,
  allowEdit,
  currencySymbol = '$',
  ...props
}) => {
  const translate = useTranslate()
  const componentName = componentType === 'battery' ? 'Battery' : 'Inverter'
  const maximumReplacementQuantityField =
    componentType === 'battery' ? 'maximum_battery_replacement_quantity' : 'maximum_inverter_replacement_quantity'

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={4}>
        <DependentFieldContainer>
          <FieldWrapper style={{ width: '100%' }}>
            <CustomField
              component={SelectInput}
              name={`end_of_life_config.${costMethodField}`}
              source={`end_of_life_config.${costMethodField}`}
              label={`${componentName} Replacement Cost Method`}
              choices={[
                { id: 'component_cost_pre_inflation', name: 'Component cost (pre-inflation)' },
                { id: 'manual_cost_pre_inflation', name: 'Manual cost (pre-inflation)' },
                {
                  id: 'manual_cost_per_unit_pre_inflation',
                  name: componentType === 'battery' ? 'Cost per kWh' : 'Cost per kW',
                },
              ]}
              disabled={!allowEdit}
              defaultValue="component_cost_pre_inflation"
              fullWidth
            />
          </FieldWrapper>
        </DependentFieldContainer>
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <DependentFieldContainer>
          <FieldWrapper style={{ width: '100%' }}>
            <NumberInput
              style={{ width: '100%' }}
              name={`end_of_life_config.${inflationField}`}
              source={`end_of_life_config.${inflationField}`}
              label={`${componentName} Replacement Cost Inflation Annual`}
              {...props}
              defaultValue={'5'}
              endAdornment={'+/-%/' + translate('year')}
              disabled={!allowEdit}
              validate={validRange(-100, 100, true, { fieldName: translate('Inflation Rate') })}
              fullWidth
            />
          </FieldWrapper>
          <TooltipIconWrapper>
            <InfoTooltip
              placement="right"
              title={
                <TooltipContainer>
                  <TooltipHeader>
                    {componentType === 'battery'
                      ? translate('Battery Replacement Cost Inflation:')
                      : translate('Inverter Replacement Cost Inflation:')}
                  </TooltipHeader>
                  <TooltipTable>
                    <TooltipRow>
                      <TooltipLabel>{translate('Positive value')}</TooltipLabel>
                      <TooltipDescriptionCell>
                        {translate('Increases the cost each year (inflation) - useful for modeling price increases')}
                      </TooltipDescriptionCell>
                    </TooltipRow>
                    <TooltipRow>
                      <TooltipLabel>{translate('Negative value')}</TooltipLabel>
                      <TooltipDescriptionCell>
                        {translate('Decreases the cost each year (deflation) - useful for modeling price decreases')}
                      </TooltipDescriptionCell>
                    </TooltipRow>
                    <TooltipRow>
                      <TooltipLabel>{translate('Zero')}</TooltipLabel>
                      <TooltipDescriptionCell>
                        {translate('Replacement cost remains the same as the base cost without inflation adjustment')}
                      </TooltipDescriptionCell>
                    </TooltipRow>
                  </TooltipTable>
                  <TooltipFooter>
                    {componentType === 'battery'
                      ? translate(
                          'The replacement cost is calculated using the selected method adjusted by this annual percentage. This allows you to model changing battery prices over time.'
                        )
                      : translate(
                          'The replacement cost is calculated using the selected method adjusted by this annual percentage. This allows you to model changing inverter prices over time.'
                        )}
                  </TooltipFooter>
                </TooltipContainer>
              }
              severity="info"
              size="small"
            />
          </TooltipIconWrapper>
        </DependentFieldContainer>
      </Grid>
      <DependentInput
        dependsOn={`end_of_life_config.${costMethodField}`}
        resolve={(val) => val === 'manual_cost_pre_inflation' || val === 'manual_cost_per_unit_pre_inflation'}
      >
        <FormDataConsumer>
          {({ formData }) => {
            const selectedMethod = formData?.end_of_life_config?.[costMethodField]
            const isCostOverride = selectedMethod === 'manual_cost_pre_inflation'
            const isDollarsPerKwh = selectedMethod === 'manual_cost_per_unit_pre_inflation'

            if (!isCostOverride && !isDollarsPerKwh) {
              return null
            }

            const unitLabel = componentType === 'battery' ? 'kWh' : 'kW'
            const label = isCostOverride
              ? `${componentName} Manual cost (pre-inflation)`
              : `${componentName} Replacement Cost per ${unitLabel}`

            let tooltipTitle
            if (isCostOverride) {
              tooltipTitle = translate('Manual cost (pre-inflation):')
            } else {
              tooltipTitle =
                componentType === 'battery'
                  ? translate('Battery Replacement Cost per kWh:')
                  : translate('Inverter Replacement Cost per kW:')
            }

            const tooltipDescription = isCostOverride
              ? componentType === 'battery'
                ? translate(
                    'Specify a fixed Manual cost (pre-inflation) for battery replacement. This cost will be adjusted by the annual inflation rate you specify.'
                  )
                : translate(
                    'Specify a fixed Manual cost (pre-inflation) for inverter replacement. This cost will be adjusted by the annual inflation rate you specify.'
                  )
              : componentType === 'battery'
              ? translate(
                  'Specify the cost per kWh capacity for battery replacement. This value will be multiplied by the battery capacity and adjusted by the annual inflation rate you specify.'
                )
              : translate(
                  'Specify the cost per kW capacity for inverter replacement. This value will be multiplied by the inverter capacity and adjusted by the annual inflation rate you specify.'
                )

            return (
              <Grid item xs={12} sm={6} md={4}>
                <DependentFieldContainer>
                  <FieldWrapper style={{ width: '100%' }}>
                    <NumberInput
                      style={{ width: '100%' }}
                      name={`end_of_life_config.${fixedValueField}`}
                      source={`end_of_life_config.${fixedValueField}`}
                      label={label}
                      {...props}
                      defaultValue={isCostOverride ? 0 : undefined}
                      endAdornment={
                        isDollarsPerKwh
                          ? `${currencySymbol}/${componentType === 'battery' ? 'kWh' : 'kW'}`
                          : undefined
                      }
                      disabled={!allowEdit}
                      validate={requiredPositiveOrZero}
                      fullWidth
                    />
                  </FieldWrapper>
                  <TooltipIconWrapper>
                    <InfoTooltip
                      placement="right"
                      title={
                        <TooltipContainer>
                          <TooltipHeader>{translate(tooltipTitle)}</TooltipHeader>
                          <TooltipTable>
                            <TooltipRow>
                              <TooltipLabel>{translate('Description')}</TooltipLabel>
                              <TooltipDescriptionCell>{tooltipDescription}</TooltipDescriptionCell>
                            </TooltipRow>
                          </TooltipTable>
                        </TooltipContainer>
                      }
                      severity="info"
                      size="small"
                    />
                  </TooltipIconWrapper>
                </DependentFieldContainer>
              </Grid>
            )
          }}
        </FormDataConsumer>
      </DependentInput>
      <Grid item xs={12} sm={6} md={4}>
        <DependentFieldContainer>
          <FieldWrapper style={{ width: '100%' }}>
            <NumberInput
              style={{ width: '100%' }}
              name={`end_of_life_config.${maximumReplacementQuantityField}`}
              source={`end_of_life_config.${maximumReplacementQuantityField}`}
              label={`Maximum Number of Replacements`}
              {...props}
              parse={(value) => (value === '' || value === null || value === undefined ? null : Number(value))}
              format={(value) => (value === null || value === undefined ? '' : value)}
              disabled={!allowEdit}
              InputLabelProps={{ shrink: true }}
              helperText={'Leave blank for unlimited'}
              fullWidth
              validate={validRange(0, 25, false, { fieldName: translate('Maximum Number of Replacements') })}
            />
          </FieldWrapper>
        </DependentFieldContainer>
      </Grid>
    </Grid>
  )
}
